import { Keys, Menu, Plate, Stage } from '@flobi/asset-library/hints';
import type { CSSProperties, ReactNode } from 'react';
import { Icon, type IconName } from '../../editor/chrome/icons';
import { ALL_TOOLS } from '../../editor/shell/Toolbar';
import { KeyCaps } from '../../ui/KeyCaps';
import { keyParts } from '../../ui/keys';

/**
 * The pictures on Artwork's hint cards, in the "Tip" style: the one real control a hint is
 * about, drawn with the editor's own classes, tokens and icons, enlarged (1×–1.5×) and centred
 * on the card's quiet surface. One focal idea and at most one motion per picture (`fh-pressed`
 * on what is pressed, or `fh-appear` on what shows up). No screens shrunk into the card.
 *
 * The card's media is 356 × 224; a `node` scene gets a 12px inset (332 × 200). Keep the
 * top-right 44 × 44 clear for the card's close button: a picture wider than 268 stays
 * under 136 tall.
 */

/** Enlarges a control crisply around its centre; layout still measures it at 1×. */
function Up({ by, children, style }: { by: number; children: ReactNode; style?: CSSProperties }) {
  return <div style={{ transform: `scale(${by})`, transformOrigin: '50% 50%', ...style }}>{children}</div>;
}

/** A tool's own name, icon and key, as the toolbar and the dock's menus say them. */
const toolMark = (id: string) => ALL_TOOLS.find((mark) => mark.id === id)!;

/**
 * A dock family's menu, as `Menu` draws it (CreateEditDock's familyItems): icon, name and key
 * per tool, with the one the hint is about lit (the menu's own fill, no ring on top of it).
 */
function FamilyMenu({ ids, lit, width }: { ids: readonly string[]; lit: string; width: number }) {
  return (
    <div
      className="menu-surface"
      style={{
        // Rows sit 6px in, so the lit row has even room round it (concentric: radius − inset).
        ['--menu-inset' as string]: '6px',
        width,
        background: 'var(--glass-thick)',
        boxShadow: 'var(--glass-edge), 0 0 0 1px var(--glass-border), var(--glass-shadow)',
        color: 'var(--label)',
      }}
    >
      <div className="menu-list">
        {ids.map((id) => {
          const mark = toolMark(id);
          const on = id === lit;
          return (
            <div key={id} className={`flex min-h-[24px] items-center gap-[7px] menu-item px-[10px] py-[3px] text-[13px] leading-[16px] ${on ? 'bg-accent text-on-accent' : 'text-label'}`}>
              <span className="grid w-[16px] shrink-0 place-items-center"><Icon name={mark.icon} size={16} /></span>
              <span className="min-w-0 flex-1 truncate leading-[18px]">{mark.label}</span>
              <KeyCaps shortcut={mark.shortcut ?? ''} lit={on} className={`shrink-0 pl-4 text-[13px] ${on ? 'text-on-accent/85' : 'text-label-3'}`} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Repeat ▸ Cloner: the dock's Repeat menu (Cloner, Blend), Cloner lit. 280 × 84 at 1.4×. */
export function ClonerScene() {
  return (
    <Up by={1.4}>
      <FamilyMenu ids={['cloner', 'blend']} lit="cloner" width={200} />
    </Up>
  );
}

/** Modify ▸ Shape Builder: the dock's Modify menu, Shape Builder pressed. 295 × 132 at 1.25× (wide enough for the whole label). */
export function BuilderScene() {
  return (
    <Up by={1.25}>
      <FamilyMenu ids={['warp', 'liquify', 'builder', 'cut']} lit="builder" width={236} />
    </Up>
  );
}

/**
 * The Shape Builder's whole-drawing buttons on the tool shelf (builder.ts `rail`, bottom row),
 * Merge by Colour pressed. Two of the three, so the strip stays readable at 1.3×.
 */
export function ByColourScene() {
  const rail: [IconName, string, boolean][] = [
    ['palette', 'Merge by Colour', true],
    ['ungroup', 'Split', false],
  ];
  return (
    <Up by={1.3}>
      <div style={{ background: 'var(--panel-solid)', borderRadius: 12, boxShadow: '0 0 0 1px var(--glass-border)' }}>
        <div className="create-edit-dock tool-shelf" style={{ display: 'flex', borderBottom: 0, padding: '5px 6px' }}>
          <div className="tool-options-strip" style={{ display: 'flex', overflow: 'visible' }}>
            <div className="flex items-center gap-[3px]">
              {rail.map(([icon, label, pressed]) => (
                <span key={icon} className={`rail-command${pressed ? ' fh-pressed' : ''}`}>
                  <Icon name={icon} size={16} />
                  <span>{label}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Up>
  );
}

/** Keycaps at the card's 12px floor (the editor's own KeyCaps are 10.5px, too small at 1.1×). */
function Caps({ shortcut }: { shortcut: string }) {
  const parts = keyParts(shortcut) ?? [shortcut];
  return (
    <span className="inline-flex items-center gap-[3px]" aria-label={parts.join(' ')}>
      {parts.map((part, index) => (
        <kbd key={index} className="inline-grid h-[20px] min-w-[20px] place-items-center rounded-[5px] bg-fill-1 px-[5px] font-sans text-[12px] font-medium leading-none text-label-2 shadow-[inset_0_0_0_1px_var(--separator)]">
          {part}
        </kbd>
      ))}
    </span>
  );
}

/**
 * The quick bar over two chosen layers (QuickBar.tsx), on a neutral surface, with Group pressed
 * (the one ring) and its help tag saying ⌘G, the shortcut the hint teaches. 297 × 96 at 1.1×.
 */
export function GroupScene() {
  return (
    <Up by={1.1} style={{ display: 'grid', justifyItems: 'start', gap: 8 }}>
      <div className="selection-bar" style={{ background: 'var(--panel-solid)', borderColor: 'var(--glass-border)' }}>
        <div className="selection-bar-properties">
          <span className="selection-bar-control fh-pressed">
            <Icon name="group" size={16} />
            <span>Group</span>
          </span>
        </div>
        <div className="selection-bar-actions">
          <span className="selection-bar-control"><span className="truncate">Arrange</span><Icon name="chevron" size={10} /></span>
          <span className="selection-bar-control selection-bar-icon"><Icon name="copy" size={16} /></span>
          <span className="selection-bar-control selection-bar-icon"><Icon name="sidebar-right" size={16} /></span>
        </div>
      </div>
      <div
        className="flex items-center gap-2 rounded-[7px] px-2 py-[5px] text-[12px] leading-[15px]"
        style={{
          marginLeft: 8,
          background: 'var(--tooltip-bg)',
          color: 'var(--tooltip-label)',
          boxShadow: '0 0 0 0.5px var(--glass-border), 0 6px 20px -6px rgb(0 0 0 / 0.45)',
          whiteSpace: 'nowrap',
        }}
      >
        <span>Group</span>
        <Caps shortcut="⌘G" />
      </div>
    </Up>
  );
}

/* ── The approved proposed hints (gallery tip/artwork.jsx, 9 Oct), built from the kit's Tip pieces ──
 * They wear the card's tokens (var(--_bg), --_menu, --_text, --_accent …), so they follow the
 * card's light or dark theme with the editor. Icons are the editor's own (`Icon`). */

/** Artwork's selection box: a violet hairline with square handles at the corners. */
function Chosen({ children }: { children: ReactNode }) {
  return (
    <div style={{ position: 'relative' }}>
      {children}
      <div style={{ position: 'absolute', inset: -4, boxShadow: '0 0 0 1.5px var(--_accent)' }}>
        {[[0, 0], [1, 0], [0, 1], [1, 1]].map(([x, y], i) => (
          <i key={i} style={{ position: 'absolute', left: `calc(${x! * 100}% - 4px)`, top: `calc(${y! * 100}% - 4px)`, width: 8, height: 8, background: '#fff', boxShadow: '0 0 0 1.5px var(--_accent)' }} />
        ))}
      </div>
    </div>
  );
}

/** The system pointer, at rest. */
const Arrow = ({ style }: { style: CSSProperties }) => (
  <svg width="16" height="20" viewBox="0 0 16 20" style={{ position: 'absolute', ...style }} aria-hidden="true">
    <path d="M1.5 1.5v14.2l3.9-3.6 2.6 6 2.7-1.2-2.6-5.9h5.4z" fill="#fff" stroke="#1b1b1d" strokeWidth="1.3" strokeLinejoin="round" />
  </svg>
);

/** A segmented control. The chosen segment's own fill is the emphasis: no ring on top of it. */
const Seg = ({ options, on, w, small }: { options: string[]; on: number; w?: number; small?: boolean }) => (
  <div className="tp-seg" style={{ width: w }}>
    {options.map((o, i) => (
      <span key={o} className={i === on ? 'on' : undefined} style={small ? { height: 26, lineHeight: '26px', padding: '0 10px' } : undefined}>{o}</span>
    ))}
  </div>
);

const T3: CSSProperties = { fontSize: 12, color: 'var(--_text-3)' };
const SHAPE: CSSProperties = { width: 64, height: 64, borderRadius: 16, background: 'color-mix(in srgb, var(--_accent) 70%, var(--_bg))' };

/** ⌥-drag: the original stays, the copy arrives under the pointer, chosen. */
export function AltDragScene() {
  return (
    <Stage scale={1.25}>
      <div style={{ position: 'relative', width: 204, height: 100 }}>
        <div style={{ position: 'absolute', left: 8, top: 18, ...SHAPE }} />
        <div className="fh-appear" style={{ position: 'absolute', left: 120, top: 18 }}>
          <Chosen><div style={SHAPE} /></Chosen>
        </div>
        <Arrow style={{ left: 128, top: 26 }} />
        <span style={{ position: 'absolute', left: 146, top: 48, minWidth: 24, height: 24, boxSizing: 'border-box', padding: '0 5px', display: 'grid', placeItems: 'center', borderRadius: 6, background: 'var(--_menu)', color: 'var(--_text)', fontSize: 14, fontWeight: 500, boxShadow: '0 0 0 1px var(--_edge), 0 2px 6px -2px rgb(0 0 0 / 30%)' }}>{keyParts('⌥')?.[0] ?? '⌥'}</span>
      </div>
    </Stage>
  );
}

/** The Cloner strip's + Effector menu (effectors.ts RECIPES), three of its recipes, Fade pressed. */
export function EffectorsScene() {
  return (
    <Stage scale={1.15}>
      <Menu
        w={200}
        pressedIndex={1}
        items={[
          { icon: <Icon name="rotate" size={16} />, label: 'Twist' },
          { icon: <Icon name="shade" size={16} />, label: 'Fade' },
          { icon: <Icon name="field-radial" size={16} />, label: 'Magnify' },
        ]}
      />
    </Stage>
  );
}

/** The tools' single keys beside their names; the Pen row is the one ringed. */
export function ToolKeysScene() {
  const rows: [IconName, string, string, boolean][] = [['pen', 'Pen', 'P', true], ['square', 'Rectangle', 'M', false], ['circle', 'Ellipse', 'L', false]];
  return (
    <Stage scale={1.3}>
      <Plate w={196} pad={8} className="tp-menu">
        {rows.map(([icon, label, key, ring]) => (
          <div key={label} className={`tp-mi${ring ? ' fh-pressed' : ''}`}>
            <span className="tp-ic"><Icon name={icon} size={16} /></span>
            <span className="tp-ml">{label}</span>
            <span className="tp-kbd" style={{ minWidth: 20, justifyContent: 'center' }}>{key}</span>
          </div>
        ))}
      </Plate>
    </Stage>
  );
}

/** The quick bar's Align menu, at its end: Distribute Horizontally pressed. */
export function DistributeScene() {
  return (
    <Stage scale={1.2}>
      <Menu
        w={220}
        pressedIndex={2}
        items={[
          { icon: <Icon name="align-bottom" size={16} />, label: 'Align Bottom Edges' },
          { sep: true },
          { icon: <Icon name="distribute-h" size={16} />, label: 'Distribute Horizontally' },
          { icon: <Icon name="distribute-v" size={16} />, label: 'Distribute Vertically' },
        ]}
      />
    </Stage>
  );
}

/** Edit ▸ Select's ways of growing a choice (EditorPage REACH_NAMES), Select Same Fill pressed. */
export function SameFillScene() {
  return (
    <Stage scale={1.15}>
      <Menu w={216} pressedIndex={1} items={[{ label: 'Shrink Selection' }, { label: 'Select Same Fill' }, { label: 'Select Same Size' }]} />
    </Stage>
  );
}

/** Properties ▸ Combine: Mask beside the four cutting operations, Mask pressed. */
export function MaskScene() {
  return (
    <Stage scale={1.35}>
      <Plate pad={0} style={{ padding: '4px 16px 12px' }}>
        <div className="tp-sec" style={{ paddingLeft: 0 }}>Combine</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span className="tp-tool fh-pressed" style={{ width: 34, height: 32 }}><Icon name="mask" size={18} /></span>
          <span style={{ width: 1, height: 18, margin: '0 4px', background: 'var(--_line)' }} />
          {(['union', 'subtract', 'intersect', 'exclude'] as const).map((op) => (
            <span key={op} className="tp-tool" style={{ width: 34, height: 32 }}><Icon name={op} size={18} /></span>
          ))}
        </div>
      </Plate>
    </Stage>
  );
}

/** Export's What row (ExportPanel): All chosen, and what that makes. */
export function ExportAllScene() {
  return (
    <Stage>
      <Plate w={240} pad={12}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--_text-2)', marginBottom: 8 }}>What</div>
        <Seg options={['Selection', 'Artboard', 'All']} on={2} />
        <div style={{ ...T3, marginTop: 6 }}>4 artboards</div>
        <div className="tp-sep" style={{ margin: '10px 0' }} />
        <div style={{ fontSize: 13 }}>4 files in a ZIP</div>
      </Plate>
    </Stage>
  );
}

/** Export's Format choices for a moving page: MP4 chosen, GIF beside it. */
export function ExportVideoScene() {
  const tile = (icon: IconName, label: string, note: string, on: boolean) => (
    <span style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 10, background: on ? 'var(--_selected)' : 'var(--_field)', boxShadow: on ? 'inset 0 0 0 1.5px var(--_accent)' : 'inset 0 0 0 1px var(--_line)', whiteSpace: 'nowrap' }}>
      <span style={{ color: 'var(--_text-2)', display: 'grid' }}><Icon name={icon} size={20} /></span>
      <span style={{ flex: 1, display: 'grid', gap: 1 }}>
        <b style={{ fontSize: 13, fontWeight: 600 }}>{label}</b>
        <small style={T3}>{note}</small>
      </span>
      {on ? <span style={{ width: 18, height: 18, marginRight: 2, borderRadius: 9, display: 'grid', placeItems: 'center', background: 'var(--_accent)', color: 'var(--fh-accent-ink, #fff)' }}><Icon name="check" size={11} /></span> : null}
    </span>
  );
  return (
    <Stage scale={1.1}>
      <Plate w={240} pad={12}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--_text-2)', marginBottom: 8 }}>Format</div>
        <div style={{ display: 'grid', gap: 8 }}>
          {tile('format-video', 'MP4', 'Video', true)}
          {tile('format-animation', 'GIF', 'Looping animation', false)}
        </div>
      </Plate>
    </Stage>
  );
}

/** The Animate bar's Timing (TimingStrip): three layers' bars on the ruler, Photo's being dragged later. */
export function TimingScene() {
  const NAME = 72, TRACK = 168, S = 3;
  const x = (s: number) => (s / S) * TRACK;
  const rows: [string, number, number, boolean][] = [['Headline', 0, 1.4, false], ['Photo', 1.5, 2.6, true], ['Logo', 1.8, 3, false]];
  return (
    <Stage>
      <Plate w={NAME + TRACK + 16} pad={0} style={{ padding: '16px 8px' }}>
        <div style={{ position: 'relative', height: 24, marginLeft: NAME, width: TRACK }}>
          {[0, 1, 2, 3].map((m) => <span key={m} style={{ position: 'absolute', left: x(m), top: 3, transform: m === 0 ? 'none' : m === S ? 'translateX(-100%)' : 'translateX(-50%)', fontSize: 12, color: 'var(--_text-3)', fontVariantNumeric: 'tabular-nums' }}>{m ? `${m}s` : '0'}</span>)}
          <span className="fh-appear" style={{ position: 'absolute', left: x(1.5), top: 2, transform: 'translateX(-50%)', padding: '0 7px', borderRadius: 999, background: 'var(--_text)', color: 'var(--_bg)', fontSize: 12, fontWeight: 500, lineHeight: '18px', whiteSpace: 'nowrap' }}>1.5s</span>
        </div>
        <div style={{ position: 'relative' }}>
          {rows.map(([name, from, until, chosen]) => (
            <div key={name} style={{ display: 'flex', alignItems: 'center', height: 30, borderRadius: 8, background: chosen ? 'color-mix(in srgb, var(--_accent) 18%, transparent)' : undefined }}>
              <span style={{ width: NAME, flex: 'none', paddingLeft: 8, boxSizing: 'border-box', fontSize: 12, whiteSpace: 'nowrap', color: chosen ? 'var(--_text)' : 'var(--_text-2)', fontWeight: chosen ? 500 : 400 }}>{name}</span>
              <span style={{ position: 'relative', width: TRACK, height: 16 }}>
                <i style={{ position: 'absolute', left: x(from), width: x(until - from), top: 0, bottom: 0, borderRadius: 6, background: chosen ? 'var(--_accent)' : 'var(--_selected)' }} />
              </span>
            </div>
          ))}
          <i style={{ position: 'absolute', left: NAME + x(1.5), top: -4, bottom: 0, width: 1.5, background: 'var(--_text-2)' }} />
        </div>
      </Plate>
    </Stage>
  );
}

/** Document Settings (PageInspector): Colour set to CMYK. */
export function CmykScene() {
  return (
    <Stage scale={1.2}>
      <Plate w={240} pad={14}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Document Settings</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 52, fontSize: 12, color: 'var(--_text-2)' }}>Colour</span>
          <Seg options={['RGB', 'CMYK']} on={1} w={140} small />
        </div>
      </Plate>
    </Stage>
  );
}

const KNOB: CSSProperties = { position: 'absolute', top: -3, width: 16, height: 16, borderRadius: 8, background: '#fff', boxShadow: '0 0 0 1px rgb(0 0 0 / 15%), 0 1px 4px rgb(0 0 0 / 30%)' };

/** The colour picker's eyedropper beside the hue and opacity bars (Colour.tsx), pressed. */
export function EyedropperScene() {
  return (
    <Stage scale={1.35}>
      <Plate w={220} pad={12}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="tp-tool fh-pressed" style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--_field)' }}><Icon name="eyedropper" size={16} /></span>
          <div style={{ flex: 1, display: 'grid', gap: 10 }}>
            <div style={{ position: 'relative', height: 10, borderRadius: 5, background: 'linear-gradient(90deg,#f00,#ff0,#0f0,#0ff,#00f,#f0f,#f00)' }}>
              <b style={{ ...KNOB, left: '72%', margin: '0 -8px' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ position: 'relative', flex: 1, height: 10, borderRadius: 5, background: 'linear-gradient(90deg, transparent, var(--_accent))', boxShadow: 'inset 0 0 0 1px var(--_line)' }}>
                <b style={{ ...KNOB, right: 0, margin: '0 -2px' }} />
              </div>
              <span style={{ fontSize: 12, color: 'var(--_text-2)', width: 34, textAlign: 'right' }}>100%</span>
            </div>
          </div>
        </div>
      </Plate>
    </Stage>
  );
}

/** The colour picker's brand line: the nearest brand colour one press away. */
export function BrandColourScene() {
  return (
    <Stage scale={1.1}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 36, width: 240, boxSizing: 'border-box', padding: '0 6px 0 12px', borderRadius: 10, background: 'var(--_field)', fontSize: 13, color: 'var(--_text-2)' }}>
        <span style={{ width: 14, height: 14, borderRadius: 7, flex: 'none', background: '#2f5bd3', boxShadow: 'inset 0 0 0 0.5px rgb(0 0 0 / 28%)' }} />
        <span style={{ flex: 1, whiteSpace: 'nowrap' }}>Nearest: <b style={{ fontWeight: 600, color: 'var(--_text)' }}>Cobalt</b></span>
        <span className="tp-btn v-secondary fh-pressed" style={{ height: 24, padding: '0 12px', fontSize: 12 }}>Use</span>
      </div>
    </Stage>
  );
}

/** The Brand tab's type (brand.tsx TextTile): Heading and Body text, Heading pressed. */
export function BrandTypeScene() {
  const tile = (text: string, big: boolean) => (
    <span className={big ? 'fh-pressed' : undefined} style={{ display: 'grid', alignItems: 'center', height: 64, padding: '0 12px', borderRadius: 10, background: 'var(--_field)', boxShadow: 'inset 0 0 0 1px var(--_line)' }}>
      <span style={big ? { fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em' } : { fontSize: 13, color: 'var(--_text-2)' }}>{text}</span>
    </span>
  );
  return (
    <Stage scale={1.3}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, width: 236 }}>
        {tile('Heading', true)}
        {tile('Body text', false)}
      </div>
    </Stage>
  );
}

/** My Stuff opens on Recent: pictures, clips and SVGs, as quiet tiles. */
export function MyStuffScene() {
  const tile = (icon: IconName) => (
    <span style={{ width: 72, height: 72, borderRadius: 10, display: 'grid', placeItems: 'center', background: 'var(--_field)', boxShadow: 'inset 0 0 0 1px var(--_line)', color: 'var(--_text-3)' }}><Icon name={icon} size={24} /></span>
  );
  return (
    <Stage scale={1.1}>
      <Plate w={256} pad={12}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
          <b style={{ fontSize: 13, fontWeight: 600 }}>Recent</b>
          <span style={{ fontSize: 12, color: 'var(--_text-2)' }}>See All</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          {tile('format-image')}
          {tile('format-video')}
          {tile('format-vector')}
        </div>
      </Plate>
    </Stage>
  );
}

/** Two keys, two views: 0 fits everything, 1 is actual size. */
export function ZoomKeysScene() {
  return (
    <Stage>
      <div style={{ display: 'flex', gap: 32 }}>
        <Keys keys={['0']} label="Fit" />
        <Keys keys={['1']} label="Actual size" />
      </div>
    </Stage>
  );
}
